import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import moment from "moment";
import Select from "./Select";
import Input from "./Input";
import TextArea from "./TextArea";
import fosterCareHomeBedOptionSets from "../../options/fostercarehomebed.json";
import generalOptionSets from "../../options/general.json";
import {
  updateEntity,
  createEntity,
  getFieldName,
  getFieldText,
  getFieldDate,
  getFieldFormattedValue,
} from "../../scripts/helperScripts";
import {
  getClientPrograms,
  getProgramServiceItemGroup,
  getProgramServiceItems,
  getServiceItemStatuses,
} from "../services/soar";

function FosterHomeBedModal({
  isOpen,
  hideModal,
  fosterHome,
  initialData,
  updateBeds,
}) {
  const [fosterHomeBed, setFosterHomeBed] = useState({});
  const [roomNumber, setRoomNumber] = useState("");
  const [bedNumber, setBedNumber] = useState("");
  const [clientPrograms, setClientPrograms] = useState([]);
  const [clientProgramOptions, setClientProgramOptions] = useState([]);
  const [clientProgramOptionsLoading, setClientProgramOptionsLoading] =
    useState(false);
  const [serviceItemOptions, setServiceItemOptions] = useState([]);
  const [serviceItemOptionsLoading, setServiceItemOptionsLoading] =
    useState(false);
  const [serviceStatusOptions, setServiceStatusOptions] = useState([]);
  const [serviceStatusOptionsLoading, setServiceStatusOptionsLoading] =
    useState(false);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  //Validation Schema
  const joiSchema = {
    ccx_fosterchildid: Joi.string(),
    ccx_name: Joi.string().allow("").label("Name"),
    svc_bedavailability: Joi.string().label("Bed Type").required(),
    svc_bedavailabilityreason: Joi.string().allow(""),
    ccx_effectivedate: Joi.date()
      .label("Effective Date")
      .max(moment().add(5, "days").toDate())
      .min(new Date("1/1/1900"))
      .allow("")
      .required()
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Effective Date cannot be future dated`;
              break;
            case "date.min":
              error.message = `Effective Date cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    ccx_expirationdate: Joi.date()
      .label("Expiration Date")
      .max("now")
      .min(
        new Date(
          moment(fosterHomeBed.ccx_effectivedate).subtract(1, "day").toDate()
        )
      )
      .allow("")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Expiration Date cannot be future dated`;
              break;
            case "date.min":
              error.message = `Expiration Date cannot be before Effective Date`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    ccx_expirationreason: Joi.string().allow(""),
    ccx_notes: Joi.string().allow(""),
    room: Joi.string().label("Room #").required(),
    bed: Joi.string().label("Bed #").required(),
    ccx_actualadmission: Joi.date()
      .label("Admission Date")
      .max("now")
      .min(
        fosterHomeBed.ccx_clientprogram !== undefined &&
          fosterHomeBed.ccx_clientprogram !== "" &&
          clientPrograms.length > 0
          ? moment(
              getFieldDate(
                clientPrograms.filter(
                  (program) =>
                    program.ccx_client_programid ===
                    fosterHomeBed.ccx_clientprogram
                )[0].ccx_admitdate
              )
            )
              .add(-1, "days")
              .toDate()
          : new Date("1/1/1900")
      )
      .allow("")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Admission Date cannot be future dated`;
              break;
            case "date.min":
              error.message = `Admission Date cannot be before ${
                fosterHomeBed.ccx_clientprogram !== undefined &&
                fosterHomeBed.ccx_clientprogram !== "" &&
                clientPrograms.length > 0
                  ? getFieldDate(
                      clientPrograms.filter(
                        (program) =>
                          program.ccx_client_programid ===
                          fosterHomeBed.ccx_clientprogram
                      )[0].ccx_admitdate
                    )
                  : "1/1/1900"
              }`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    ccx_actualdischarge: Joi.date()
      .label("Discharge Date")
      .max("now")
      .min(new Date(fosterHomeBed.ccx_actualadmission))
      .allow("")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Discharge Date cannot be future dated`;
              break;
            case "date.min":
              error.message = `Discharge Date cannot be before ${
                fosterHomeBed.ccx_actualadmission !== undefined &&
                fosterHomeBed.ccx_actualadmission !== ""
                  ? fosterHomeBed.ccx_actualadmission
                  : "Admission Date."
              }`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    ccx_clientprogram: Joi.string().label("Client Program"),
    ccx_clientdiagnosis: Joi.string(),
    ccx_serviceitem: Joi.string().label("Service Item"),
    ccx_servicestatuslookup: Joi.string().label("Service Status"),
    svc_dischargetransferreason: Joi.string().allow(""),
    ccx_contact: Joi.string().allow("", null),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (initialData) {
      const obj = {
        ccx_fosterchildid: initialData.ccx_fosterchildid,
        ccx_name: initialData.ccx_name,
        svc_bedavailability: getFieldText(initialData.svc_bedavailability),
        svc_bedavailabilityreason: getFieldText(
          initialData.svc_bedavailabilityreason
        ),
        ccx_effectivedate: getFieldDate(initialData.ccx_effectivedate),
        ccx_expirationdate: getFieldDate(initialData.ccx_expirationdate),
        ccx_expirationreason: getFieldText(initialData.ccx_expirationreason),
        ccx_notes: initialData.ccx_notes,
        ccx_contact: getFieldName(initialData.ccx_contact),
        ccx_actualadmission: getFieldDate(initialData.ccx_actualadmission),
        ccx_clientprogram:
          getFieldText(initialData.ccx_clientprogram) ||
          getFieldFormattedValue(initialData.ccx_clientprogram),
        ccx_clientdiagnosis: getFieldName(initialData.ccx_clientdiagnosis),
        ccx_serviceitem: getFieldText(initialData.ccx_serviceitem),
        ccx_servicestatuslookup: getFieldText(
          initialData.ccx_servicestatuslookup
        ),
        svc_dischargetransferreason: getFieldText(
          initialData.svc_dischargetransferreason
        ),
        ccx_actualdischarge: getFieldDate(initialData.ccx_actualdischarge),
      };

      const roomIndex = obj.ccx_name.toLowerCase().indexOf("room ");
      const bedIndex = obj.ccx_name.toLowerCase().indexOf("bed ");

      let roomNumber = "";
      let bedNumber = "";

      if (roomIndex !== -1) {
        roomNumber = obj.ccx_name
          .slice(roomIndex + 5, bedIndex - 1)
          .replace("-", "")
          .replace(",", "")
          .trimEnd();
      }

      if (bedIndex !== -1) {
        const bedSlice = obj.ccx_name.slice(bedIndex + 4);
        const hyphenIndex = bedSlice.indexOf(" - ");
        if (hyphenIndex !== -1) bedNumber = bedSlice.slice(0, hyphenIndex);
        else bedNumber = bedSlice;
      }

      obj.room = roomNumber;
      obj.bed = bedNumber;

      setRoomNumber(roomNumber);
      setBedNumber(obj.bed);

      setFosterHomeBed(obj);

      if (initialData.ccx_contact !== undefined) {
        const loadClientProgramOptions = async () => {
          setClientProgramOptionsLoading(true);

          const programs = await getClientPrograms(
            getFieldText(initialData.ccx_contact)
          );

          if (isMountedRef.current) {
            if (programs) {
              setClientPrograms(programs);

              const options = programs
                .filter(
                  (program) =>
                    program.ccx_dischargedate === undefined &&
                    getFieldName(program["unit.ccx_programtype"]) ===
                      "Foster Home"
                )
                .map((program) => {
                  const option = {
                    _id: program.ccx_client_programid,
                    name: `${getFieldName(program.ccx_programunit)} - ${moment(
                      getFieldText(program.ccx_admitdate)
                    ).format("L")}${
                      program.ccx_dischargedate
                        ? ` - ${moment(
                            getFieldText(program.ccx_dischargedate)
                          ).format("L")}`
                        : ""
                    }`,
                    admitDate: getFieldDate(program.ccx_admitdate),
                  };

                  return option;
                });

              setClientProgramOptions(options);
            }

            setClientProgramOptionsLoading(false);
          }
        };

        loadClientProgramOptions();
      }
    }
  }, [initialData]);

  useEffect(() => {
    if (fosterHomeBed.ccx_clientprogram !== null && clientPrograms.length > 0) {
      const selectedProgram = clientPrograms.filter(
        (program) =>
          program.ccx_client_programid === fosterHomeBed.ccx_clientprogram
      )[0];

      if (selectedProgram) {
        const loadServiceItemOptions = async (programUnitId) => {
          setServiceItemOptionsLoading(true);

          const response = await getProgramServiceItemGroup(
            programUnitId,
            safyInfo.user.safyInfo.BusinessUnitIdName
          );
          if (isMountedRef.current) {
            if (response && response[0]) {
              const response2 = await getProgramServiceItems(
                response[0].ccx_cost_center_categoryid,
                safyInfo.user.safyInfo.BusinessUnitIdName
              );

              if (isMountedRef.current) {
                if (response2) {
                  const options = response2
                    .filter(
                      (item) => getFieldName(item.ccx_needs_bed) === "Yes"
                    )
                    .map((item) => ({
                      _id: item.ccx_service_itemid,
                      name: item.ccx_name,
                    }));

                  setServiceItemOptions(options);
                }

                setServiceItemOptionsLoading(false);
              }
            } else {
              setServiceItemOptionsLoading(false);
            }
          }
        };

        loadServiceItemOptions(getFieldText(selectedProgram.ccx_programunit));
      }
    }
  }, [fosterHomeBed.ccx_clientprogram, clientPrograms]);

  useEffect(() => {
    if (
      fosterHomeBed.ccx_serviceitem !== undefined &&
      fosterHomeBed.ccx_serviceitem !== ""
    ) {
      const loadServiceItemStatuses = async (selectedServiceItem) => {
        setServiceStatusOptionsLoading(true);

        const response = await getServiceItemStatuses(selectedServiceItem);
        if (isMountedRef.current) {
          if (response) {
            const options = response.map((status) => ({
              _id: status.ccx_servicestatusid,
              name: status.ccx_status,
            }));

            setServiceStatusOptions(options);
          }

          setServiceStatusOptionsLoading(false);
        }
      };

      loadServiceItemStatuses(fosterHomeBed.ccx_serviceitem);
    }
  }, [fosterHomeBed.ccx_serviceitem]);

  const handleSave = () => {
    const selectedBedType =
      fosterCareHomeBedOptionSets.svc_bedavailability.filter(
        (option) => option._id === fosterHomeBed.svc_bedavailability
      )[0];

    let ccx_name = `${fosterHome.ccx_name}${
      roomNumber !== "" && ` Room ${roomNumber}`
    }${bedNumber !== "" && ` Bed ${bedNumber}`}`;

    if (selectedBedType) ccx_name += ` - ${selectedBedType.name}`;

    const obj = {
      ccx_name: {
        type: "string",
        value: ccx_name,
      },
      ccx_fostercarehome: {
        type: "entity",
        value: {
          entityName: "ccx_fostercarehomeaddress",
          entityId: fosterHome.ccx_fostercarehomeid,
        },
      },
      svc_bedavailability: {
        type: "optionset",
        value:
          fosterHomeBed.svc_bedavailability !== undefined &&
          fosterHomeBed.svc_bedavailability !== ""
            ? parseInt(fosterHomeBed.svc_bedavailability)
            : null,
      },
      svc_bedavailabilityreason: {
        type: "optionset",
        value:
          fosterHomeBed.svc_bedavailabilityreason !== undefined &&
          fosterHomeBed.svc_bedavailabilityreason !== ""
            ? parseInt(fosterHomeBed.svc_bedavailabilityreason)
            : null,
      },
      ccx_effectivedate: {
        type: "datetime",
        value: new Date(fosterHomeBed.ccx_effectivedate),
      },
      ccx_expirationdate: {
        type: "datetime",
        value:
          fosterHomeBed.ccx_expirationdate !== undefined &&
          fosterHomeBed.ccx_expirationdate !== ""
            ? new Date(fosterHomeBed.ccx_expirationdate)
            : null,
      },
      ccx_expirationreason: {
        type: "optionset",
        value:
          fosterHomeBed.ccx_expirationreason !== undefined &&
          fosterHomeBed.ccx_expirationreason !== ""
            ? parseInt(fosterHomeBed.ccx_expirationreason)
            : null,
      },
      ccx_notes: {
        type: "string",
        value: fosterHomeBed.ccx_notes,
      },
      ccx_actualadmission: {
        type: "datetime",
        value: new Date(fosterHomeBed.ccx_actualadmission),
      },
      ccx_clientprogram: {
        type: "entity",
        value: {
          entityName: "ccx_client_program",
          entityId: fosterHomeBed.ccx_clientprogram,
        },
      },
      ccx_serviceitem: {
        type: "entity",
        value: {
          entityName: "ccx_service_item",
          entityId: fosterHomeBed.ccx_serviceitem,
        },
      },
      ccx_servicestatuslookup: {
        type: "entity",
        value: {
          entityName: "ccx_service_item",
          entityId: fosterHomeBed.ccx_servicestatuslookup,
        },
      },
      svc_dischargetransferreason: {
        type: "optionset",
        value:
          fosterHomeBed.svc_dischargetransferreason !== undefined &&
          fosterHomeBed.svc_dischargetransferreason !== ""
            ? parseInt(fosterHomeBed.svc_dischargetransferreason)
            : null,
      },
      ccx_actualdischarge: {
        type: "datetime",
        value: new Date(fosterHomeBed.ccx_actualdischarge),
      },
    };

    if (fosterHomeBed.ccx_fosterchildid !== undefined) {
      const toastId = toast.loading("Foster Home Bed is being updated...", {
        type: toast.TYPE.INFO,
      });
      updateEntity(
        "ccx_fosterchild",
        fosterHomeBed.ccx_fosterchildid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          toast.update(toastId, {
            render: "Foster Home Bed has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
          updateBeds && updateBeds(true);
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating the Foster Home Bed.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        });
    } else {
      const toastId = toast.loading("Foster Home Bed is being created...", {
        type: toast.TYPE.INFO,
      });
      createEntity(
        "ccx_fosterchild",
        JSON.stringify(obj),
        safyInfo,
        getFieldName(fosterHome.owningbusinessunit).split(" of ")[1]
      )
        .then(() => {
          toast.update(toastId, {
            render: "Foster Home Bed has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
          updateBeds && updateBeds(true);
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while creating the Foster Home Bed.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        });
    }
  };

  const handleSaveAndAdd = async () => {
    handleSave();
    resetForm();
  };

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);

    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
    }

    const data = { ...fosterHomeBed };
    data[input.name] = input.value;
    setFosterHomeBed(data);

    setErrors(errs);
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(fosterHomeBed, joiSchema, options);

    let validationErrors = {};
    if (error) {
      for (let item of error.details)
        validationErrors[item.path[0]] = item.message;
    }

    if (
      fosterHomeBed.svc_dischargetransferreason !== undefined &&
      fosterHomeBed.svc_dischargetransferreason !== "" &&
      (fosterHomeBed.ccx_actualdischarge === undefined ||
        fosterHomeBed.ccx_actualdischarge === "")
    )
      validationErrors.ccx_actualdischarge = "'Actual Discharge' is required.";

    if (fosterHomeBed.ccx_contact !== undefined) {
      if (
        fosterHomeBed.ccx_clientprogram === undefined ||
        fosterHomeBed.ccx_clientprogram === ""
      )
        validationErrors.ccx_clientprogram = "'Client Program' is required.";

      if (
        fosterHomeBed.ccx_actualadmission === undefined ||
        fosterHomeBed.ccx_actualadmission === ""
      )
        validationErrors.ccx_actualadmission = "'Admission Date' is required.";

      if (
        fosterHomeBed.ccx_serviceitem === undefined ||
        fosterHomeBed.ccx_serviceitem === ""
      )
        validationErrors.ccx_serviceitem = "'Service Item' is required.";

      if (
        fosterHomeBed.ccx_servicestatuslookup === undefined ||
        fosterHomeBed.ccx_servicestatuslookup === ""
      )
        validationErrors.ccx_servicestatuslookup =
          "'Service Status' is required.";

      if (
        fosterHomeBed.svc_dischargetransferreason !== undefined &&
        fosterHomeBed.svc_dischargetransferreason !== ""
      )
        if (
          fosterHomeBed.ccx_actualdischarge === undefined ||
          fosterHomeBed.ccx_actualdischarge === ""
        )
          validationErrors.ccx_actualdischarge =
            "'Discharge Date' is required.";
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) return null;
    return validationErrors;
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const resetForm = () => {
    setFosterHomeBed({
      ccx_name: "",
      ccx_effectivedate: fosterHomeBed.ccx_effectivedate || "",
      svc_bedavailability: "",
      svc_bedavailabilityreason: "",
      ccx_expirationdate: "",
      ccx_expirationreason: "",
    });
    setRoomNumber("");
    setBedNumber("");
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  const handleDeactivateBed = () => {
    const obj = {
      statecode: {
        type: "status",
        value: 1,
      },
    };

    const toastId = toast.loading("Deactivating Foster Home Bed...", {
      type: toast.TYPE.INFO,
    });
    updateEntity(
      "ccx_fosterchild",
      fosterHomeBed.ccx_fosterchildid,
      JSON.stringify(obj),
      safyInfo
    )
      .then(() => {
        toast.update(toastId, {
          render: "Foster Home Bed has been deactivated.",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          closeOnClick: true,
          autoClose: 5000,
        });
        updateBeds && updateBeds(true);
      })
      .catch(() => {
        toast.update(toastId, {
          render: "An error occurred while deactivating the Foster Home Bed.",
          type: toast.TYPE.ERROR,
          isLoading: false,
          closeOnClick: true,
          autoClose: 5000,
        });
      });
  };

  return (
    <>
      {isOpen ? (
        <Modal
          show={isOpen}
          animation={false}
          onHide={() => {
            return;
          }}
        >
          <Modal.Header>
            <Modal.Title>Foster Home Bed</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container">
              <div className="row">
                <div className="col">
                  <div className="row mb-4">
                    <div className="col text-center">{fosterHome.ccx_name}</div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="room"
                        label="Room #"
                        value={roomNumber}
                        onChange={(e) => {
                          handleChange(e);
                          setRoomNumber(e.currentTarget.value);
                        }}
                        error={errors.room}
                        disabled={
                          fosterHomeBed.ccx_actualdischarge !== undefined &&
                          fosterHomeBed.ccx_actualdischarge !== ""
                        }
                      />
                    </div>
                    <div className="col">
                      <Input
                        name="bed"
                        label="Bed #"
                        value={bedNumber}
                        onChange={(e) => {
                          handleChange(e);
                          setBedNumber(e.currentTarget.value);
                        }}
                        error={errors.bed}
                        disabled={
                          fosterHomeBed.ccx_actualdischarge !== undefined &&
                          fosterHomeBed.ccx_actualdischarge !== ""
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="ccx_effectivedate"
                    label="Effective Date"
                    type="date"
                    value={
                      fosterHomeBed.ccx_effectivedate &&
                      moment
                        .utc(fosterHomeBed.ccx_effectivedate)
                        .format("YYYY-MM-DD")
                    }
                    onChange={handleChange}
                    error={errors.ccx_effectivedate}
                    disabled={
                      fosterHomeBed.ccx_actualdischarge !== undefined &&
                      fosterHomeBed.ccx_actualdischarge !== ""
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Select
                    name="svc_bedavailability"
                    label="Bed Type"
                    options={fosterCareHomeBedOptionSets.svc_bedavailability}
                    value={fosterHomeBed.svc_bedavailability}
                    onChange={handleChange}
                    error={errors.svc_bedavailability}
                    disabled={
                      fosterHomeBed.ccx_actualdischarge !== undefined &&
                      fosterHomeBed.ccx_actualdischarge !== ""
                    }
                  />
                </div>
                <div className="col">
                  <Select
                    name="svc_bedavailabilityreason"
                    label="Bed Type Reason"
                    options={
                      fosterCareHomeBedOptionSets.svc_bedavailabilityreason
                    }
                    value={fosterHomeBed.svc_bedavailabilityreason}
                    onChange={handleChange}
                    error={errors.svc_bedavailabilityreason}
                    disabled={
                      fosterHomeBed.ccx_actualdischarge !== undefined &&
                      fosterHomeBed.ccx_actualdischarge !== ""
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="ccx_expirationdate"
                    label="Expiration Date"
                    type="date"
                    value={
                      fosterHomeBed.ccx_expirationdate &&
                      moment
                        .utc(fosterHomeBed.ccx_expirationdate)
                        .format("YYYY-MM-DD")
                    }
                    onChange={handleChange}
                    error={errors.ccx_expirationdate}
                    disabled={
                      fosterHomeBed.ccx_actualdischarge !== undefined &&
                      fosterHomeBed.ccx_actualdischarge !== ""
                    }
                  />
                </div>
                <div className="col">
                  <Select
                    name="ccx_expirationreason"
                    label="Expiration Reason"
                    options={generalOptionSets.ccx_expirationreason}
                    value={fosterHomeBed.ccx_expirationreason}
                    onChange={handleChange}
                    error={errors.ccx_expirationreason}
                    disabled={
                      fosterHomeBed.ccx_actualdischarge !== undefined &&
                      fosterHomeBed.ccx_actualdischarge !== ""
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <TextArea
                    name="ccx_notes"
                    label="Notes"
                    value={fosterHomeBed.ccx_notes}
                    onChange={handleChange}
                    disabled={
                      fosterHomeBed.ccx_actualdischarge !== undefined &&
                      fosterHomeBed.ccx_actualdischarge !== ""
                    }
                  />
                </div>
              </div>
              {fosterHomeBed.ccx_contact !== undefined ? (
                <>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="ccx_contact"
                        label="Client"
                        value={fosterHomeBed.ccx_contact}
                        disabled
                      />
                    </div>
                    <div className="col">
                      <Input
                        name="ccx_actualadmission"
                        label="Admission Date"
                        value={
                          fosterHomeBed.ccx_actualadmission &&
                          moment
                            .utc(fosterHomeBed.ccx_actualadmission)
                            .format("YYYY-MM-DD")
                        }
                        onChange={handleChange}
                        error={errors.ccx_actualadmission}
                        type="date"
                        disabled={
                          fosterHomeBed.ccx_actualdischarge !== undefined &&
                          fosterHomeBed.ccx_actualdischarge !== ""
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Select
                        name="ccx_clientprogram"
                        label="Client Program"
                        options={clientProgramOptions}
                        value={fosterHomeBed.ccx_clientprogram}
                        onChange={handleChange}
                        error={errors.ccx_clientprogram}
                        disabled={
                          (fosterHomeBed.ccx_actualdischarge !== undefined &&
                            fosterHomeBed.ccx_actualdischarge !== "") ||
                          clientProgramOptionsLoading
                        }
                      />
                    </div>
                    <div className="col">
                      <Input
                        name="ccx_clientdiagnosis"
                        label="Bed Diagnosis"
                        value={fosterHomeBed.ccx_clientdiagnosis}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Select
                        name="ccx_serviceitem"
                        label="Service Item"
                        options={serviceItemOptions}
                        value={fosterHomeBed.ccx_serviceitem}
                        onChange={handleChange}
                        error={errors.ccx_serviceitem}
                        disabled={
                          (fosterHomeBed.ccx_actualdischarge !== undefined &&
                            fosterHomeBed.ccx_actualdischarge !== "") ||
                          serviceItemOptionsLoading
                        }
                      />
                    </div>
                    <div className="col">
                      <Select
                        name="ccx_servicestatuslookup"
                        label="Service Status"
                        options={serviceStatusOptions}
                        value={fosterHomeBed.ccx_servicestatuslookup}
                        onChange={handleChange}
                        error={errors.ccx_servicestatuslookup}
                        disabled={
                          (fosterHomeBed.svc_dischargetransferreason !==
                            undefined &&
                            fosterHomeBed.svc_dischargetransferreason !== "") ||
                          serviceStatusOptionsLoading
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Select
                        name="svc_dischargetransferreason"
                        label="Discharge Reason"
                        options={
                          fosterCareHomeBedOptionSets.svc_dischargetransferreason
                        }
                        value={fosterHomeBed.svc_dischargetransferreason}
                        onChange={({ currentTarget: input }) => {
                          const errs = { ...errors };
                          const errorMessage = validateProperty(input);

                          if (errorMessage) {
                            errs[input.name] = errorMessage;
                            console.log("Errors:", JSON.stringify(errs));
                          } else {
                            delete errs[input.name];
                          }

                          const data = { ...fosterHomeBed };
                          data[input.name] = input.value;
                          data.ccx_servicestatuslookup =
                            "{315A3B59-0AEB-E211-90BC-005056822045}";
                          setFosterHomeBed(data);

                          setErrors(errs);
                        }}
                        error={errors.svc_dischargetransferreason}
                        disabled={
                          fosterHomeBed.ccx_actualdischarge !== undefined &&
                          fosterHomeBed.ccx_actualdischarge !== ""
                        }
                      />
                    </div>
                    <div className="col">
                      <Input
                        name="ccx_actualdischarge"
                        label="Discharge Date"
                        value={
                          fosterHomeBed.ccx_actualdischarge &&
                          moment
                            .utc(fosterHomeBed.ccx_actualdischarge)
                            .format("YYYY-MM-DD")
                        }
                        onChange={handleChange}
                        error={errors.ccx_actualdischarge}
                        type="date"
                        disabled={
                          fosterHomeBed.svc_dischargetransferreason ===
                            undefined ||
                          fosterHomeBed.svc_dischargetransferreason === ""
                        }
                      />
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </Modal.Body>
          <Modal.Footer>
            {fosterHomeBed.ccx_contact === undefined ? (
              <button className="btn btn-warning" onClick={handleDeactivateBed}>
                Deactivate
              </button>
            ) : (
              <Link
                className="btn btn-success"
                id="BDS"
                title="Open Bed Day Statuses"
                to="/BDS"
                state={{
                  contactName: fosterHomeBed.ccx_contact,
                  bedId: fosterHomeBed.ccx_fosterchildid,
                  bedName: fosterHomeBed.ccx_name,
                }}
              >
                BDS
              </Link>
            )}
            <button className="btn btn-danger" onClick={handleHideModal}>
              Cancel
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) {
                  handleSave();
                  handleHideModal();
                }
              }}
            >
              Submit
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) handleSaveAndAdd();
              }}
            >
              Submit and Add New
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default FosterHomeBedModal;
